/** The query param a preview link carries, in the window query and in a hash route alike. */
export const CHANGE_SET_PARAM = 'changeSet';

/**
 * Change set ids are UUIDs (v7 in practice, so they sort by age). Anything else in the URL is a
 * typo or someone's own param and is ignored rather than sent to the service as a header.
 */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isChangeSetId = (value?: string | null): value is string =>
  typeof value === 'string' && UUID.test(value);

/**
 * The change set id a preview link carries, from either address the embed can be routed by.
 *
 * In query mode (the default) it rides the real query string —
 * `…/brands/<brandId>?changeSet=<uuid>` — and stays there across in-app navigation: `changeSet`
 * is neither a route key nor an app param, so `encodeRoute` carries it through untouched.
 *
 * In hash mode the whole route lives behind the fragment (`#!/e/slug?changeSet=<uuid>`), so the
 * id rides the fragment's own query string; both hash types (`#!/…` and `#/…`) read the same way,
 * since only what follows the first `?` is looked at.
 *
 * The window query is read first, so appending the param to a page's URL works even on a host
 * page that keeps a stale fragment around.
 */
export const getChangeSetIdFromUrl = (
  { hash, search }: Pick<Location, 'hash' | 'search'> = window.location,
): string | undefined => {
  const fromSearch = new URLSearchParams(search).get(CHANGE_SET_PARAM);
  if (isChangeSetId(fromSearch)) return fromSearch;

  const at = hash.indexOf('?');
  if (at === -1) return undefined;

  const fromHash = new URLSearchParams(hash.slice(at)).get(CHANGE_SET_PARAM);
  return isChangeSetId(fromHash) ? fromHash : undefined;
};
